import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  getOccurrenceCandidates,
  searchFranchiseCoaches,
  saveEvaluationResolution,
  removeEvaluationResolution,
} from '../services/coachIdentityApi';
import { summarizeSessionStaff, SUMMARY_INITIAL_LIMIT } from '../utils/sessionStaffSummary';
import { FORM_COACH_DEFAULT_NOTE, FORM_LABEL_MAX, suggestFormCoachLabel } from '../utils/formCoachDefaults';
import ScopedRemoveDialog from './ScopedRemoveDialog';

const ANY_FACILITY = '__any__';

// Why a suggestion could not be narrowed by time. Shown so a refusal reads as a deliberate refusal
// rather than a silent failure to search — the auditor then broadens rather than mistrusting.
const TIME_REFUSAL = {
  ambiguous_decimal_shorthand: 'the Form time is ambiguous shorthand (e.g. "9.3" could be 9:03 or 9:30)',
  names_multiple_times: 'the Form time names more than one class',
  unparseable: 'the Form time is not a readable time',
  no_time_entered: 'no time was entered on the Form',
};

function fmtDate(v) {
  return v ? String(v).slice(0, 10) : '—';
}

// One candidate session in words: class, date, start time.
function candidateText(c) {
  return `${c.class_name} · ${fmtDate(c.occurrence_date)} ${String(c.event_start_time || '').slice(0, 5)}`.trim();
}

// How the Form's class-type answer relates to the Dash catalogue, in the auditor's words.
const FORM_LEVEL_NOTE = {
  mapped: (lvl) => `matches Dash level ${lvl.level_name}`,
  unmapped: () => 'no exact Dash level — not used to narrow candidates',
  missing: () => 'not recorded on the Form',
};

const unique = (xs) => [...new Map(xs.map((x) => [x.employee_id, x])).values()];

// Class outcomes and their reasons. The reason set is fixed (migration 003 ck_lksi_cer_reason
// mirrors it); the case-specific explanation belongs in the required note.
export const CLASS_OUTCOME = Object.freeze({
  CONFIRMED: 'occurrence_confirmed',
  UNRESOLVED: 'occurrence_unresolved',
  NONE: 'no_dash_occurrence_expected',
});
const CLASS_OUTCOME_LABEL = {
  [CLASS_OUTCOME.CONFIRMED]: 'Confirm a Dash class session',
  [CLASS_OUTCOME.UNRESOLVED]: 'Class can’t be determined',
  [CLASS_OUTCOME.NONE]: 'No Dash class expected',
};
export const REASON_LABEL = Object.freeze({
  class_ambiguous: 'Several sessions fit; the evidence does not single one out',
  form_dash_conflict: 'The Form and Dash evidence conflict',
  insufficient_form_detail: 'The Form does not give enough detail',
  no_session_on_date: 'Dash has no session on the submitted date',
  not_scheduled_in_dash: 'This franchise or program is not scheduled in Dash',
});
const REASONS_FOR = {
  [CLASS_OUTCOME.UNRESOLVED]: ['class_ambiguous', 'form_dash_conflict', 'insufficient_form_detail'],
  [CLASS_OUTCOME.NONE]: ['no_session_on_date', 'not_scheduled_in_dash'],
};
export const COACH_MODE = Object.freeze({ UNDECIDED: 'undecided', DASH: 'dash_employee', FORM: 'form_identity' });
const COACH_MODE_LABEL = {
  [COACH_MODE.UNDECIDED]: 'Not decided yet',
  [COACH_MODE.DASH]: 'A Dash employee',
  [COACH_MODE.FORM]: 'Keep the Form’s coach (this evaluation only)',
};

// Everything a save persists, in one comparable string, so "has anything changed since the stored
// review?" is one comparison. Built the same way from the controls and from a stored review, and
// normalised the way the server stores it (trimmed text; class id only with a confirmed class; the
// reason only without one; the employee only for a Dash coach; the label only for a Form coach).
function reviewSignature({ status, event, reason, coach, employee, label, note }) {
  const confirmed = status === CLASS_OUTCOME.CONFIRMED;
  return JSON.stringify([
    status || '',
    confirmed ? String(event || '') : '',
    confirmed ? '' : String(reason || ''),
    coach || '',
    coach === COACH_MODE.DASH ? String(employee || '') : '',
    coach === COACH_MODE.FORM ? String(label || '').trim() : '',
    String(note || '').trim(),
  ]);
}

// The stored review's signature, defaulted exactly as the controls are seeded from it below.
function storedSignature(e) {
  if (!e.is_reconciled) return null;
  return reviewSignature({
    status: e.resolution_status || CLASS_OUTCOME.CONFIRMED,
    event: e.scheduled_occurrence_id,
    reason: e.reason_code,
    coach: e.coach_resolution || (e.selected_employee_id ? COACH_MODE.DASH : ''),
    employee: e.selected_employee_id,
    label: e.resolved_form_coach_label,
    note: e.resolution_note,
  });
}

/**
 * The class picker for ONE evaluation.
 *
 * 🔴 SAVES AT checkin_id GRAIN AND NOWHERE ELSE. Selecting a class here reconciles this one
 * evaluation. It does not touch the identity-wide decision above, and it does not apply to the
 * identity's other evaluations — those are a different grain, table and endpoint.
 *
 * Reporting reads the saved coach judgment (slice 2b) at the next successful check-in warehouse
 * build, whichever scheduled run that is. The server
 * returns `reporting_integration_pending` and the scope caption below is driven from it, so the
 * message cannot drift from what the warehouse is doing. Defaults to pending when not passed.
 */
export default function CoachIdentityCandidatePanel({
  evaluation, facilities = [], onResolved, onSaved, identityRuleActive = false, focusOnMount = false,
  integrationPending = true,
}) {
  const [relaxTime, setRelaxTime] = useState(false);
  const [relaxProgram, setRelaxProgram] = useState(false);
  // Any level: show every candidate instead of only those agreeing with the Form class type.
  const [anyLevel, setAnyLevel] = useState(false);
  const [showAllStaff, setShowAllStaff] = useState(false);
  const [date, setDate] = useState(fmtDate(evaluation.class_date));
  const [facility, setFacility] = useState(evaluation.facility_id || ANY_FACILITY);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // A failed CANDIDATE READ, kept apart from `error` (which reports save and selection problems).
  // Measured 2026-09-23: these failures come from Azure SQL serverless reading a cold data cache,
  // surfacing as a generic 500 -- so the message states only that loading failed, never a cause the
  // client cannot see.
  const [loadFailed, setLoadFailed] = useState(false);
  // Only the newest request may apply, and a retry cannot start while one is in flight.
  const requestSeq = useRef(0);
  const inFlight = useRef(false);

  // 🔴 INSPECTING AND CONFIRMING ARE TWO STATES (gap log 7, 2026-09-24). They once shared one radio
  // selection, so clicking a class to look at its coaches silently became the saved class. Now
  // inspecting only focuses a candidate; `selectedEvent` -- the class that will be saved -- changes
  // only through the explicit Confirm button.
  const [inspectedEvent, setInspectedEvent] = useState(evaluation.scheduled_occurrence_id || '');
  const [selectedEvent, setSelectedEvent] = useState(evaluation.scheduled_occurrence_id || '');
  // What the summary names when the confirmed class is not in the current search results (the
  // auditor broadened or narrowed after confirming, or reopened a saved review).
  const [confirmedText, setConfirmedText] = useState(
    evaluation.scheduled_occurrence_id
      ? [evaluation.snapshot_class_label,
        evaluation.snapshot_occurrence_date && fmtDate(evaluation.snapshot_occurrence_date),
        evaluation.snapshot_start_time && String(evaluation.snapshot_start_time).slice(0, 5)]
        .filter(Boolean).join(' · ') || evaluation.scheduled_occurrence_id
      : '',
  );
  const [selectedEmployee, setSelectedEmployee] = useState(evaluation.selected_employee_id || '');
  // The off-roster coach as an OBJECT, not just an id: once chosen they are by definition absent
  // from the class roster, so there is nothing else to render their name and status from.
  const [offRoster, setOffRoster] = useState(null);
  const [coachQuery, setCoachQuery] = useState('');
  const [coachResults, setCoachResults] = useState(null);
  const [coachSearching, setCoachSearching] = useState(false);
  const [coachError, setCoachError] = useState('');
  const [note, setNote] = useState(evaluation.resolution_note || '');
  const [saving, setSaving] = useState(false);
  // 🔴 SYNCHRONOUS in-flight guard. `saving` disables the button, but state only reaches the DOM on
  // the next render; the ref refuses a second request even if a second click arrives before it.
  const savingRef = useRef(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  // What is stored for this evaluation, as a signature. Save stays disabled while the controls match
  // it: re-saving an unchanged review only wrote a duplicate row (7 identical re-saves, 2026-09-25).
  const [savedSig, setSavedSig] = useState(() => storedSignature(evaluation));
  // An evaluation opened by advancing gets keyboard focus, so the auditor carries straight on.
  const headingRef = useRef(null);
  useEffect(() => {
    if (focusOnMount && headingRef.current) headingRef.current.focus({ preventScroll: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- on mount only

  // ───────────────────────────── the two judgments this save records (evaluation review, 2026-09-23)
  // CLASS: which Dash session this evaluation describes -- or an explicit "cannot be determined" /
  // "no Dash class expected", neither of which ever carries a class id.
  // COACH: a Dash employee, the Form's coach kept for THIS evaluation only, or not decided yet.
  // Both are explicit human acts; nothing here is preselected from evidence.
  const [classOutcome, setClassOutcome] = useState(evaluation.resolution_status || CLASS_OUTCOME.CONFIRMED);
  const [reasonCode, setReasonCode] = useState(evaluation.reason_code || '');
  const [coachMode, setCoachMode] = useState(
    evaluation.coach_resolution || (evaluation.selected_employee_id ? COACH_MODE.DASH : COACH_MODE.UNDECIDED),
  );
  const [formLabel, setFormLabel] = useState(evaluation.resolved_form_coach_label || '');
  const classConfirmed = classOutcome === CLASS_OUTCOME.CONFIRMED;
  const noteRequired = !classConfirmed || coachMode === COACH_MODE.FORM;

  // READ ONLY. Loads candidates for the CURRENT controls; used both by the controls themselves and by
  // Retry, so a retry repeats exactly the search on screen. Nothing here selects or saves: the saved
  // selection lives in selectedEvent/selectedEmployee and is untouched by a failure or a retry.
  const load = useCallback(() => {
    const mine = ++requestSeq.current;
    inFlight.current = true;
    setLoading(true);
    setError('');
    setLoadFailed(false);
    getOccurrenceCandidates({
      checkin_id: evaluation.checkin_id,
      relax_time: relaxTime,
      relax_program: relaxProgram,
      date,
      facility_id: facility === ANY_FACILITY ? '' : facility,
    })
      .then((res) => { if (mine === requestSeq.current) setData(res); })
      .catch(() => {
        if (mine !== requestSeq.current) return;
        setData(null);
        setLoadFailed(true);
      })
      .finally(() => {
        if (mine !== requestSeq.current) return;
        inFlight.current = false;
        setLoading(false);
      });
  }, [evaluation.checkin_id, relaxTime, relaxProgram, date, facility]);

  useEffect(() => { load(); }, [load]);

  const retry = () => {
    if (inFlight.current) return;   // never two concurrent retries
    load();
  };

  const coachesByTeam = useMemo(() => {
    const map = new Map();
    ((data && data.coaches) || []).forEach((c) => {
      if (!map.has(c.team_dim_id)) map.set(c.team_dim_id, []);
      map.get(c.team_dim_id).push(c);
    });
    return map;
  }, [data]);

  // 🔴 THE FULL FRANCHISE FACILITY LIST, NOT ONE DERIVED FROM THE CURRENT RESULTS. An earlier
  // version built these options from the facilities that happened to appear in the candidates
  // already returned, which meant the auditor could only reach a facility by first broadening to
  // "Any" and hoping it surfaced -- and a facility with no class in the current search was simply
  // unreachable. The list now comes from dim_facility scoped to this evaluation's franchise, so
  // every facility is selectable directly and picking one narrows the occurrence query itself.
  //
  // The 200-row cap still applies to candidate SESSIONS, where it belongs. It never governed this
  // list: the largest franchise has 32 facilities, and at most 19 of any franchise's facilities
  // carry classes at all.
  //
  // The Form's own facility is added when it is somehow absent from the dimension, so a broken
  // Form value can still be seen and searched rather than silently vanishing from the control.
  const facilityOptions = useMemo(() => {
    const seen = new Map();
    facilities.forEach((f) => seen.set(f.facility_id, {
      name: f.facility_name || f.facility_id,
      inactive: f.is_active === 0,
    }));
    if (evaluation.facility_id && !seen.has(evaluation.facility_id)) {
      seen.set(evaluation.facility_id, { name: evaluation.facility_name || evaluation.facility_id, inactive: false });
    }
    return [...seen.entries()];
  }, [facilities, evaluation.facility_id, evaluation.facility_name]);

  // Staffing of each DATED SESSION, when the API provides it. `null` means an older API response:
  // the panel then shows the class roster exactly as before.
  const sessionByEvent = useMemo(() => {
    if (!data || !Array.isArray(data.session_staff)) return null;
    const map = new Map();
    data.session_staff.forEach((s) => {
      if (!map.has(s.event_id)) map.set(s.event_id, []);
      map.get(s.event_id).push(s);
    });
    // One entry per (event_id, employee_id), even if a duplicate slipped through.
    map.forEach((rows, k) => map.set(k, unique(rows)));
    return map;
  }, [data]);

  // ───────────────────────────────────────────── the Form class type: evidence, not truth
  // Candidates that agree with the Form's class-type answer are shown by default. The narrowing is
  // always announced ("2 of 6 …") and undone by Any level, so a filtered list can never pass for the
  // whole population. When the answer agrees with NO candidate, nothing is hidden: the full list
  // stays, with a warning. Nothing here selects or saves.
  const allCandidates = useMemo(() => (data && data.candidates) || [], [data]);
  const formLevel = (data && data.form_level) || null;
  const levelMapped = Boolean(formLevel && formLevel.status === 'mapped');
  const matchingCount = allCandidates.filter((c) => c.form_level_match === true).length;
  const contradictsAll = levelMapped && allCandidates.length > 0 && matchingCount === 0;
  const levelNarrowing = levelMapped && matchingCount > 0 && !anyLevel;
  const visibleCandidates = levelNarrowing
    // A class already confirmed or being inspected stays visible even if it disagrees with the Form,
    // so a saved or in-progress choice can never silently disappear behind the default view.
    ? allCandidates.filter((c) => c.form_level_match === true || c.event_id === selectedEvent || c.event_id === inspectedEvent)
    : allCandidates;

  const selectedCandidate = useMemo(
    () => allCandidates.find((c) => c.event_id === selectedEvent) || null,
    [allCandidates, selectedEvent],
  );

  // ───────────────────────────── staff recorded on these candidate sessions (evidence only)
  // Everyone Dash staffed on ANY candidate this search returned, including candidates hidden by the
  // Form-level filter, one row per person. Built only from data already in the response. It never
  // selects, ranks or suggests anyone, and never touches the class or coach controls: whether a typed
  // Form name refers to one of these people is the auditor's call.
  const staffSummary = summarizeSessionStaff({
    candidates: allCandidates,
    sessionStaff: data ? data.session_staff : undefined,
    coaches: (data && data.coaches) || [],
    visibleEventIds: new Set(visibleCandidates.map((c) => c.event_id)),
  });
  const staffPeople = staffSummary
    ? (showAllStaff ? staffSummary.people : staffSummary.people.slice(0, SUMMARY_INITIAL_LIMIT))
    : [];
  const staffRows = staffPeople.map((p) => {
    const classText = p.classes
      .map((c) => `${c.class_name}${c.level ? ` · ${c.level}` : ''}${c.hidden ? ' · outside Form-level filter' : ''}`)
      .join('; ');
    return (
      <Typography key={p.employee_id} variant="caption" color="text.secondary" component="div">
        <strong>{p.name}{p.exactFormName ? ' ★' : ''}</strong> — {classText}
      </Typography>
    );
  });
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const selectedCoaches = selectedCandidate ? (coachesByTeam.get(selectedCandidate.team_dim_id) || []) : [];

  // 🔴 Never auto-selects. A search returns names; choosing one stays an explicit act, exactly as
  // the roster ★ is evidence rather than a decision.
  const runCoachSearch = async (term) => {
    setCoachQuery(term);
    setCoachError('');
    if (term.trim().length < 2) { setCoachResults(null); return; }
    setCoachSearching(true);
    try {
      const res = await searchFranchiseCoaches({ checkin_id: evaluation.checkin_id, search: term });
      setCoachResults(res);
    } catch (err) {
      setCoachError(err.message || 'Could not search coaches.');
      setCoachResults(null);
    } finally { setCoachSearching(false); }
  };

  const pickOffRoster = (coach) => {
    setOffRoster(coach);
    setSelectedEmployee(coach.employee_id);
    setCoachMode(COACH_MODE.DASH);   // choosing a person IS the coach judgment
    setError('');
  };

  const changeClassOutcome = (value) => {
    setClassOutcome(value);
    setReasonCode('');
    setError('');
    // Leaving "confirm a class" drops any session chosen for it: an unresolved / no-class judgment
    // never carries a class id. A Dash coach chosen OFF the roster survives (it never depended on a
    // class); one chosen FROM that class's roster is cleared with the class.
    if (value !== CLASS_OUTCOME.CONFIRMED) {
      setSelectedEvent('');
      setConfirmedText('');
      if (!offRoster) { setSelectedEmployee(''); if (coachMode === COACH_MODE.DASH) setCoachMode(COACH_MODE.UNDECIDED); }
    }
  };

  // Looking only. Never touches the class outcome, the reason, the coach, the note or the label.
  const inspectCandidate = (c) => { setInspectedEvent(c.event_id); };

  // Discards every coach judgment made against the previously confirmed class, on the roster or off
  // it: carrying one across would attribute a coach chosen for one class to a different class.
  const dropCoachForClassChange = () => {
    setSelectedEmployee(''); setOffRoster(null);
    setCoachQuery(''); setCoachResults(null);
    if (coachMode === COACH_MODE.DASH) setCoachMode(COACH_MODE.UNDECIDED);
  };

  // The ONLY way a class becomes the class to save. Confirming IS the class judgment "confirmed".
  const confirmCandidate = (c) => {
    if (c.event_id !== selectedEvent) dropCoachForClassChange();
    setSelectedEvent(c.event_id);
    setConfirmedText(candidateText(c));
    setClassOutcome(CLASS_OUTCOME.CONFIRMED); setReasonCode('');
    setError('');
  };

  const clearConfirmed = () => {
    dropCoachForClassChange();
    setSelectedEvent('');
    setConfirmedText('');
    setError('');
  };

  const changeCoachMode = (value) => {
    setCoachMode(value);
    setError('');
    if (value !== COACH_MODE.DASH) { setSelectedEmployee(''); setOffRoster(null); }
    // The label draft -- suggested or typed -- is KEPT across coach modes; it is only sent when the
    // Form coach is the outcome. Switching modes to look at an option never destroys typed text.
    //
    // Choosing the Form coach offers editable defaults, each only into a BLANK field. Nothing else
    // (exploring candidates, the search controls, a load) ever fills or refills them.
    if (value === COACH_MODE.FORM && coachMode !== COACH_MODE.FORM) {
      if (!note.trim()) setNote(FORM_COACH_DEFAULT_NOTE);
      if (!formLabel.trim()) setFormLabel(suggestFormCoachLabel(evaluation));
    }
    // Leaving it withdraws the standard note ONLY while it is exactly unchanged, since it would then
    // claim a Form coach was retained. An edited note is the auditor's and stays.
    if (value !== COACH_MODE.FORM && coachMode === COACH_MODE.FORM && note === FORM_COACH_DEFAULT_NOTE) setNote('');
  };

  // The fast path for an obvious staffed coach: ONE explicit act sets the class to save AND the Dash
  // coach. 🔴 It persists nothing -- Save is still the only write. Leaving a Form-coach outcome goes
  // through changeCoachMode, so the label draft is kept and only an unchanged standard note goes.
  const applyClassAndCoach = (c, staff, rosterEntry) => {
    confirmCandidate(c);
    changeCoachMode(COACH_MODE.DASH);
    setSelectedEmployee(staff.employee_id);
    // A staffed coach missing from the class roster is shown the way an off-roster pick is.
    setOffRoster(rosterEntry ? null : { employee_id: staff.employee_id, coach_display_name: staff.coach_display_name });
  };

  // A coach already saved against this evaluation who is not on the current class's roster shows in
  // the off-roster slot too, so reopening a saved reconciliation never renders an empty picker with
  // a coach silently still attached.
  const rosterHasSelected = selectedCoaches.some((c) => String(c.employee_id) === String(selectedEmployee));
  const effectiveOffRoster = offRoster
    || (selectedEmployee && !rosterHasSelected
      ? { employee_id: selectedEmployee, coach_display_name: evaluation.snapshot_coach_display_name || selectedEmployee }
      : null);

  const currentSig = reviewSignature({
    status: classOutcome,
    event: selectedEvent,
    reason: reasonCode,
    coach: coachMode === COACH_MODE.UNDECIDED ? '' : coachMode,
    employee: selectedEmployee,
    label: formLabel,
    note,
  });
  const unchanged = savedSig !== null && currentSig === savedSig;

  const handleSave = async () => {
    if (savingRef.current || unchanged) return;
    // Client-side checks mirror the server's contract, so the auditor learns what is missing before a
    // round trip. The server re-validates everything.
    if (classConfirmed && !selectedCandidate) { setError('Select the class session this evaluation describes.'); return; }
    if (!classConfirmed && !reasonCode) { setError('Choose a reason for the class outcome.'); return; }
    if (coachMode === COACH_MODE.DASH && !selectedEmployee) { setError('Choose the Dash employee, or change the coach outcome.'); return; }
    if (noteRequired && !note.trim()) {
      setError('Add a note explaining this conclusion — it is required when no class is confirmed or the Form coach is kept.');
      return;
    }
    const label = formLabel.trim();
    const sentSig = currentSig;
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      // coach_resolution is always sent EXPLICITLY. The class ids travel only with a confirmed class,
      // the reason only without one; nothing about a class is ever invented.
      const updated = await saveEvaluationResolution({
        checkin_id: evaluation.checkin_id,
        resolution_status: classOutcome,
        scheduled_occurrence_id: classConfirmed ? selectedCandidate.event_id : undefined,
        scheduled_team_id: classConfirmed ? selectedCandidate.team_dim_id : undefined,
        reason_code: classConfirmed ? undefined : reasonCode,
        coach_resolution: coachMode === COACH_MODE.UNDECIDED ? null : coachMode,
        selected_employee_id: coachMode === COACH_MODE.DASH ? selectedEmployee : undefined,
        resolved_form_coach_label: coachMode === COACH_MODE.FORM && label ? label : undefined,
        reviewer_note: note.trim() || undefined,
      });
      if (updated) {
        // Stored now: Save stays disabled until something changes. Then the list advances -- which
        // usually unmounts this panel -- and the page announces "Saved ✓".
        setSavedSig(sentSig);
        (onSaved || onResolved)(updated);
      }
    } catch (err) {
      // A failed save changes nothing: every selection stays exactly as it was, and Save is enabled.
      setError(err.message || 'Could not save this review.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Runs only from the confirmation dialog, never from the section button directly.
  const handleRemove = async () => {
    setConfirmRemove(false);
    setSaving(true);
    setError('');
    try {
      const updated = await removeEvaluationResolution({ checkin_id: evaluation.checkin_id });
      if (updated) onResolved(updated);
      setSavedSig(null);   // nothing is stored any more, so any complete review can be saved
      setSelectedEvent('');
      setInspectedEvent('');
      setConfirmedText('');
      setSelectedEmployee('');
      setOffRoster(null);
      setClassOutcome(CLASS_OUTCOME.CONFIRMED);
      setReasonCode('');
      setCoachMode(COACH_MODE.UNDECIDED);
      setFormLabel('');
      if (note === FORM_COACH_DEFAULT_NOTE) setNote('');
    } catch (err) {
      setError(err.message || 'Could not remove this evaluation’s review.');
    } finally { setSaving(false); }
  };

  // The SAVED review in the auditor's words, for the confirmation. Read from the evaluation (what is
  // stored), never from the controls (what may have been edited since the save).
  const savedClassText = evaluation.resolution_status === CLASS_OUTCOME.CONFIRMED
    ? [evaluation.snapshot_class_label,
      evaluation.snapshot_occurrence_date && fmtDate(evaluation.snapshot_occurrence_date),
      evaluation.snapshot_start_time && String(evaluation.snapshot_start_time).slice(0, 5)]
      .filter(Boolean).join(' · ') || CLASS_OUTCOME_LABEL[CLASS_OUTCOME.CONFIRMED]
    : [CLASS_OUTCOME_LABEL[evaluation.resolution_status], REASON_LABEL[evaluation.reason_code]]
      .filter(Boolean).join(' — ') || 'not recorded';
  const savedCoachText = evaluation.coach_resolution === COACH_MODE.FORM
    ? `Form coach${evaluation.resolved_form_coach_label ? ` “${evaluation.resolved_form_coach_label}”` : ''}`
    : evaluation.selected_employee_id
      ? (evaluation.snapshot_coach_display_name || evaluation.selected_employee_id)
      : 'not decided';
  const typedName = evaluation.typed_coach_name || evaluation.typed_name_norm;

  // Closed facilities are listed, not hidden: evaluations are historical and a class at a site that
  // has since shut is still the class that was evaluated. The marker says which is which.
  const facilityItems = facilityOptions.map(([id, f]) => (
    <MenuItem key={id} value={id}>{f.name}{f.inactive ? ' · closed' : ''}</MenuItem>
  ));

  const coachLabel = (r) => `${r.coach_display_name}${r.matches_form_name ? ' ★' : ''}${r.is_active === 0 ? ' (inactive)' : ''}`;

  // Who Dash staffed on THIS dated session, then anyone else on the all-season roster. Where the
  // session has no staffing rows, the roster is shown under an explicit label rather than passed off
  // as the session's staff.
  const staffingLines = (c) => {
    const roster = coachesByTeam.get(c.team_dim_id) || [];
    if (sessionByEvent === null) {
      return [`coaches: ${roster.length === 0 ? 'no configured coach' : roster.map(coachLabel).join(', ')}`];
    }
    const session = sessionByEvent.get(c.event_id) || [];
    if (session.length === 0) {
      return roster.length === 0
        ? ['No staffing recorded for this session, and no class roster.']
        : [`No staffing recorded for this session — showing all-season class roster: ${roster.map(coachLabel).join(', ')}`];
    }
    // Roster rows carry the Form-name match and active status; use them where the employee is known.
    const rosterById = new Map(roster.map((r) => [String(r.employee_id), r]));
    const staffed = session.map((s) => rosterById.get(String(s.employee_id)) || s);
    const sessionIds = new Set(session.map((s) => String(s.employee_id)));
    const rosterOnly = roster.filter((r) => !sessionIds.has(String(r.employee_id)));
    const lines = [`Staffed this session: ${staffed.map(coachLabel).join(', ')}`];
    if (rosterOnly.length) lines.push(`On class roster, not staffed this session: ${rosterOnly.map(coachLabel).join(', ')}`);
    return lines;
  };

  // One button per coach STAFFED ON THIS SESSION, on the inspected card only. None is preselected:
  // each is a separate explicit act. No session staffing (or an older API response) means no buttons.
  const fastPathButtons = (c) => {
    const session = sessionByEvent ? (sessionByEvent.get(c.event_id) || []) : [];
    const roster = new Map((coachesByTeam.get(c.team_dim_id) || []).map((r) => [String(r.employee_id), r]));
    return session.map((s) => {
      const r = roster.get(String(s.employee_id));
      const name = (r && r.coach_display_name) || s.coach_display_name || s.employee_id;
      const marks = `${r && r.matches_form_name ? ' ★' : ''}${r && r.is_active === 0 ? ' (inactive)' : ''}${r ? '' : ' (not on class roster)'}`;
      const isSet = c.event_id === selectedEvent && coachMode === COACH_MODE.DASH
        && String(selectedEmployee) === String(s.employee_id);
      return (
        <Button key={s.employee_id} size="small" variant="outlined" disabled={isSet}
          onClick={() => applyClassAndCoach(c, s, r)}>
          {isSet ? `✓ Class and coach set: ${name}` : `Use this class and coach: ${name}${marks}`}
        </Button>
      );
    });
  };

  // Candidates are CARDS WITH AN INSPECT BUTTON, deliberately not radio buttons: a radio reads as
  // choosing an answer, and that is exactly how an exploratory click was saved as the class (row 20).
  // Two separate markers say which card is being looked at and which one will be saved.
  const candidateItems = visibleCandidates.map((c) => {
    const isInspected = c.event_id === inspectedEvent;
    const isConfirmed = c.event_id === selectedEvent;
    const staffLines = staffingLines(c).map((line) => (
      <Typography key={line} variant="caption" color="text.secondary" component="div">{line}</Typography>
    ));
    return (
      <Box
        component="li"
        key={c.event_id}
        aria-label={c.class_name}
        aria-current={isInspected ? 'true' : undefined}
        sx={{
          listStyle: 'none', mb: 1, p: 1, borderRadius: 1, maxWidth: 760,
          border: 1,
          borderLeftWidth: isConfirmed || isInspected ? 4 : 1,
          borderColor: isConfirmed ? 'success.main' : isInspected ? 'info.main' : 'divider',
          bgcolor: isInspected ? 'action.hover' : 'transparent',
        }}
      >
        <Typography variant="body2">
          {c.class_name}
          {isConfirmed && (
            <Chip size="small" label="Confirmed class to save" color="success" sx={{ ml: 1 }} />
          )}
          {isInspected && (
            <Chip size="small" label="Currently inspecting" color="info" variant="outlined" sx={{ ml: 1 }} />
          )}
          {c.form_level_match === true && (
            <Chip size="small" label="Form level match" color="success" variant="outlined" sx={{ ml: 1 }} />
          )}
          {c.class_status && c.class_status !== 'Active' && (
            <Chip size="small" label={c.class_status} variant="outlined" sx={{ ml: 1 }} />
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {fmtDate(c.occurrence_date)} {String(c.event_start_time || '').slice(0, 5)} ·{' '}
          {c.canonical_level_name || 'no level'} · {c.facilityname || c.dim_facility_id} · {c.program_code}
        </Typography>
        {staffLines}
        <Box sx={{ mt: 0.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {!isInspected && (
            <Button size="small" variant="text" aria-label={`Inspect ${c.class_name}`} onClick={() => inspectCandidate(c)}>
              Inspect
            </Button>
          )}
          {isInspected && !isConfirmed && (
            <Button size="small" variant="contained" onClick={() => confirmCandidate(c)}>
              Confirm this session as the evaluated class
            </Button>
          )}
          {isInspected && fastPathButtons(c)}
        </Box>
      </Box>
    );
  });

  const coachSearchItems = ((coachResults && coachResults.coaches) || []).map((c) => (
    <li key={c.employee_id} style={{ listStyle: 'none', marginBottom: 2 }}>
      <Button size="small" variant="text" sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
        onClick={() => pickOffRoster(c)}>
        {c.coach_display_name || c.employee_id}
        {/* Inactive coaches are offered on purpose: these evaluations are historical and a coach
            who has since left is often the right answer. Labelled so it is never a surprise. */}
        {c.is_active === 0 ? ' · inactive' : ''}
        {c.home_facility_name ? ` · ${c.home_facility_name}` : ''}
      </Button>
    </li>
  ));

  // The picker still lists the class roster -- the same set the saved in-staffing snapshot is judged
  // against -- and says, per coach, whether Dash staffed them on this session.
  const selectedSession = selectedCandidate && sessionByEvent ? (sessionByEvent.get(selectedCandidate.event_id) || []) : null;
  const selectedSessionIds = new Set((selectedSession || []).map((s) => String(s.employee_id)));
  const sessionTag = (c) => {
    if (!selectedSession || selectedSession.length === 0) return '';
    return selectedSessionIds.has(String(c.employee_id)) ? ' · staffed this session' : ' · on class roster only';
  };
  const coachItems = selectedCoaches.map((c) => (
    <MenuItem key={c.employee_id} value={c.employee_id}>
      {c.coach_display_name}{c.matches_form_name ? ' ★ matches Form name' : ''}{sessionTag(c)}{c.is_active === 0 ? ' · inactive' : ''}
    </MenuItem>
  ));
  // A coach chosen through "Search another franchise coach" is shown IN the picker as its selected
  // value, labelled as off-roster. Without this the picker read as empty after an off-roster pick,
  // which looked like the choice had been refused when it had in fact been recorded for saving.
  const offRosterId = effectiveOffRoster ? String(effectiveOffRoster.employee_id) : '';
  if (effectiveOffRoster) {
    coachItems.push(
      <MenuItem key={`off-${offRosterId}`} value={offRosterId}>
        {effectiveOffRoster.coach_display_name || offRosterId} (not on this class&rsquo;s roster)
      </MenuItem>,
    );
  }
  const classOutcomeItems = Object.values(CLASS_OUTCOME).map((v) => (
    <MenuItem key={v} value={v}>{CLASS_OUTCOME_LABEL[v]}</MenuItem>
  ));
  const reasonItems = (REASONS_FOR[classOutcome] || []).map((r) => (
    <MenuItem key={r} value={r}>{REASON_LABEL[r]}</MenuItem>
  ));
  const coachModeItems = Object.values(COACH_MODE).map((v) => (
    <MenuItem key={v} value={v}>{COACH_MODE_LABEL[v]}</MenuItem>
  ));
  const coachPickerValue = rosterHasSelected || effectiveOffRoster ? String(selectedEmployee) : '';
  const onCoachPicked = (value) => {
    // Re-picking the off-roster entry keeps it; any other choice (a roster coach, or "Do not record
    // a coach") replaces it.
    if (effectiveOffRoster && String(value) === offRosterId) return;
    setSelectedEmployee(value);
    setOffRoster(null);
    // Picking a roster coach is the coach judgment; "Do not record a coach" leaves it undecided.
    setCoachMode(value ? COACH_MODE.DASH : COACH_MODE.UNDECIDED);
  };

  // What Save will record for the coach, in words, beside the class it will record.
  const selectedName = (selectedCoaches.find((r) => String(r.employee_id) === String(selectedEmployee)) || {}).coach_display_name
    || (effectiveOffRoster && effectiveOffRoster.coach_display_name) || selectedEmployee;
  const coachToSaveText = coachMode === COACH_MODE.FORM
    ? `Form coach “${formLabel.trim() || evaluation.typed_coach_name || '—'}”`
    : coachMode === COACH_MODE.DASH && selectedEmployee
      ? `${selectedName} (Dash${!rosterHasSelected ? ', not on class roster' : selectedSessionIds.has(String(selectedEmployee)) ? ', staffed this session' : ''})`
      : 'Not decided yet';

  return (
    <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'divider', mt: 1 }}>
      {/* Raw Form evidence, verbatim and never edited — the thing being reconciled AGAINST. */}
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        Form says: {evaluation.facility_name || 'no facility'}
        {evaluation.facility_source && evaluation.facility_source !== 'warehouse' ? ` (${evaluation.facility_source})` : ''}
        {' · '}{fmtDate(evaluation.class_date)} · &ldquo;{evaluation.class_time}&rdquo; · {evaluation.program}
      </Typography>
      {formLevel && (
        // The raw answer, verbatim, then what it means in Dash terms. Never edited.
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          Form class type:{' '}
          {formLevel.raw !== null && formLevel.raw !== undefined && String(formLevel.raw).trim() !== ''
            ? <>&ldquo;{formLevel.raw}&rdquo; — </>
            : null}
          {(FORM_LEVEL_NOTE[formLevel.status] || FORM_LEVEL_NOTE.unmapped)(formLevel)}
        </Typography>
      )}

      <Typography variant="subtitle2" component="h4" sx={{ mb: 1, outline: 'none' }} tabIndex={-1} ref={headingRef}>
        This evaluation’s review
      </Typography>

      {/* ── JUDGMENT 1 of 2: the class. "Can't be determined" and "No Dash class expected" are
             complete answers, and neither ever records a class id. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel id={`cls-${evaluation.checkin_id}`}>Class outcome</InputLabel>
          <Select labelId={`cls-${evaluation.checkin_id}`} label="Class outcome" value={classOutcome}
            onChange={(e) => changeClassOutcome(e.target.value)}>
            {classOutcomeItems}
          </Select>
        </FormControl>
        {!classConfirmed && (
          <FormControl size="small" sx={{ minWidth: 380 }}>
            <InputLabel id={`rsn-${evaluation.checkin_id}`}>Reason</InputLabel>
            <Select labelId={`rsn-${evaluation.checkin_id}`} label="Reason" value={reasonCode}
              onChange={(e) => { setReasonCode(e.target.value); setError(''); }}>
              {reasonItems}
            </Select>
          </FormControl>
        )}
      </Box>
      {!classConfirmed && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          No class is recorded for this evaluation. The candidate search below stays available as evidence;
          inspecting a session changes nothing, and only its Confirm button switches the outcome back to a
          confirmed class.
        </Typography>
      )}
      {classConfirmed && (
        // What Save will record, stated apart from whatever is being inspected below.
        <Box component="section" aria-label="Class to save" sx={{ mb: 1.5 }}>
          {selectedEvent ? (
            <Typography variant="body2" component="div">
              Confirmed class to save: <strong>{selectedCandidate ? candidateText(selectedCandidate) : confirmedText}</strong>
              <Button size="small" sx={{ ml: 1 }} disabled={saving} onClick={clearConfirmed}>Clear confirmed class</Button>
              {!selectedCandidate && !loading && (
                <Typography variant="caption" color="warning.main" component="div">
                  Not in the current search results — change the search back to save it.
                </Typography>
              )}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No class confirmed yet — inspect a session below, then press its Confirm button.
            </Typography>
          )}
          <Typography variant="body2" component="div">
            Coach to save: <strong>{coachToSaveText}</strong>
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
        <TextField size="small" type="date" label="Date" value={date}
          onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 165 }} />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id={`fac-${evaluation.checkin_id}`}>Facility</InputLabel>
          <Select labelId={`fac-${evaluation.checkin_id}`} label="Facility" value={facility}
            onChange={(e) => setFacility(e.target.value)}>
            <MenuItem value={ANY_FACILITY}>Any facility in this franchise</MenuItem>
            {facilityItems}
          </Select>
        </FormControl>
        <FormControlLabel control={<Switch size="small" checked={relaxTime} onChange={(e) => setRelaxTime(e.target.checked)} />}
          label={<Typography variant="body2">Any time</Typography>} />
        <FormControlLabel control={<Switch size="small" checked={relaxProgram} onChange={(e) => setRelaxProgram(e.target.checked)} />}
          label={<Typography variant="body2">Any program</Typography>} />
        {levelMapped && matchingCount > 0 && (
          <FormControlLabel control={<Switch size="small" checked={anyLevel} onChange={(e) => setAnyLevel(e.target.checked)} />}
            label={<Typography variant="body2">Any level</Typography>} />
        )}
      </Box>

      {/* 🔴 The franchise is never a control. The server derives it from the checkin_id and ignores
          anything a client sends, so no search can reach another franchise's classes. */}
      <Typography variant="caption" color="text.disabled" component="div" sx={{ mb: 1 }}>
        Searching within {evaluation.com_code || 'this franchise'} only — franchise cannot be changed.
      </Typography>

      {data && data.search && data.search.time_applied === false && !relaxTime && (
        <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
          Time was not used to narrow these results because {TIME_REFUSAL[data.search.time_parse_reason] || 'the Form time could not be read'}.
          Everything at this facility on this date is listed.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {data && data.truncated && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          More than {data.max_rows} sessions matched, so this list is incomplete. Narrow the date or facility.
        </Alert>
      )}

      {!loading && contradictsAll && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          The Form class type (&ldquo;{formLevel.raw}&rdquo;) matches none of the {allCandidates.length} candidate
          class{allCandidates.length === 1 ? '' : 'es'}. All candidates are shown. The Form answer may be wrong, or
          the class may be elsewhere — try another date, time or facility.
        </Alert>
      )}

      {!loading && levelMapped && matchingCount > 0 && (
        // Always stated, filtered or not, so the list on screen is never mistaken for the whole set.
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {anyLevel
            ? `Showing all ${allCandidates.length} candidate classes — ${matchingCount} match the Form class type.`
            : `${matchingCount} of ${allCandidates.length} candidate classes match the Form class type.`}
          {!anyLevel && matchingCount < allCandidates.length && ' Turn on Any level to see the others.'}
        </Typography>
      )}

      {!loading && !loadFailed && staffSummary && allCandidates.length > 0 && (
        <Box
          component="section"
          aria-label="Staff recorded on these candidate sessions"
          sx={{ mb: 1.5, p: 1, border: 1, borderColor: 'divider', borderRadius: 1, maxWidth: 760 }}
        >
          <Typography variant="body2">
            Staff recorded on these candidate sessions ({plural(staffSummary.people.length, 'person', 'people')})
          </Typography>
          <Typography variant="caption" color="text.disabled" component="div" sx={{ mb: 0.5 }}>
            Dash session staffing for every candidate in this search, including classes outside the Form-level
            filter. Evidence only — it does not say who was evaluated.
          </Typography>
          {levelNarrowing && staffSummary.hiddenCandidatesWithStaff > 0 && (
            <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
              Staff are recorded on {plural(staffSummary.hiddenCandidatesWithStaff, 'additional candidate class', 'additional candidate classes')}{' '}
              outside the Form-level filter.
              <Button size="small" sx={{ ml: 1 }} onClick={() => setAnyLevel(true)}>Show all levels</Button>
            </Typography>
          )}
          {staffRows}
          {staffSummary.people.length > SUMMARY_INITIAL_LIMIT && (
            <Button size="small" onClick={() => setShowAllStaff(!showAllStaff)}>
              {showAllStaff ? 'Show fewer' : `Show all ${staffSummary.people.length}`}
            </Button>
          )}
          {staffSummary.unstaffedCandidates > 0 && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              {plural(staffSummary.unstaffedCandidates, 'candidate session has', 'candidate sessions have')} no staffing recorded.
            </Typography>
          )}
        </Box>
      )}

      {!loading && loadFailed && (
        // A failed read is NOT an empty result: the "no session matches" message below is suppressed,
        // because it would tell the auditor there is no class when the search never completed.
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={<Button color="inherit" size="small" onClick={retry} disabled={loading}>Retry</Button>}
        >
          Candidate classes could not be loaded. The database may still be warming up.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ py: 2 }}><CircularProgress size={22} /></Box>
      ) : loadFailed ? null : !data ? null : candidateItems.length === 0 ? (
        <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
          No Dash class session matches this search. Try Any time, another date, or Any facility.
        </Alert>
      ) : (
        <Box component="ul" aria-label="Candidate class sessions" sx={{ p: 0, m: 0 }}>
          {candidateItems}
        </Box>
      )}

      {/* ── JUDGMENT 2 of 2: the coach. Available whatever the class outcome -- a coach can be known
             when the class cannot. */}
      <Box sx={{ mt: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 340 }}>
          <InputLabel id={`cm-${evaluation.checkin_id}`}>Coach outcome</InputLabel>
          <Select labelId={`cm-${evaluation.checkin_id}`} label="Coach outcome" value={coachMode}
            onChange={(e) => changeCoachMode(e.target.value)}>
            {coachModeItems}
          </Select>
        </FormControl>
      </Box>

      {coachMode === COACH_MODE.FORM && (
        // The Form's coach, kept for THIS evaluation only. The raw Form value is shown read-only and is
        // never rewritten; the optional label is auditor-provided display text, not a Dash employee,
        // and never an identity-wide rule.
        <Box component="section" aria-label="Form coach for this evaluation"
          sx={{ mt: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, maxWidth: 560 }}>
          <Typography variant="body2">
            Form name (as submitted): <strong>{evaluation.typed_coach_name || '—'}</strong>
          </Typography>
          <TextField fullWidth size="small" sx={{ mt: 1 }}
            label="Auditor-provided Form-coach label (optional)"
            placeholder={evaluation.typed_coach_name || ''}
            value={formLabel}
            inputProps={{ maxLength: FORM_LABEL_MAX }}
            onChange={(e) => setFormLabel(e.target.value)} />
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            Applies to this evaluation only. Not a Dash employee. The suggestion (Form name — class date — Form
            class type) is a contextual default, not a unique identity: evaluations with the same label may be
            grouped as one Form-only identity. Replace it with a shared label (e.g. &ldquo;Abby 1&rdquo;) to group
            evaluations deliberately.
          </Typography>
        </Box>
      )}

      {coachMode !== COACH_MODE.FORM && (
        <Box sx={{ mt: 1.5 }}>
          {/* 1. The class's CONFIGURED coaches come first: they are the likeliest answer and the
                 only ones Dash itself asserts. */}
          {selectedCandidate && (
          <FormControl size="small" fullWidth sx={{ maxWidth: 460, mb: 1.5 }}>
            <InputLabel id={`coach-${evaluation.checkin_id}`}>Evaluated coach (optional)</InputLabel>
            <Select labelId={`coach-${evaluation.checkin_id}`} label="Evaluated coach (optional)"
              value={coachPickerValue}
              onChange={(e) => onCoachPicked(e.target.value)}>
              <MenuItem value=""><em>Do not record a coach</em></MenuItem>
              {coachItems}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {selectedCoaches.length === 0
                ? 'This class has no configured coach in Dash. The class may still be saved without one.'
                : '★ marks a coach whose name matches the typed Form name. That is evidence, not proof of who taught.'}
            </Typography>
          </FormControl>
          )}

          {/* 2. A deliberately SEPARATE control. Dash staffing is the configured roster, not an
                 attendance record: substitutes are real and rosters go stale. Kept visually apart
                 so choosing from here reads as the exception it is. */}
          <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, maxWidth: 560 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>Search another franchise coach</Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              For a substitute, or a coach Dash never had configured on this class. Searches{' '}
              {evaluation.com_code || 'this franchise'} only — the franchise cannot be changed.
            </Typography>
            <TextField fullWidth size="small" label="Coach name" value={coachQuery}
              onChange={(e) => runCoachSearch(e.target.value)} />

            {coachError && <Alert severity="error" sx={{ mt: 1 }}>{coachError}</Alert>}
            {coachSearching && <Typography variant="caption" color="text.disabled">Searching…</Typography>}

            {coachQuery.trim().length > 0 && coachQuery.trim().length < 2 && (
              <Typography variant="caption" color="text.disabled" component="div" sx={{ mt: 0.5 }}>
                Type at least 2 characters.
              </Typography>
            )}

            {coachResults && coachSearchItems.length === 0 && !coachSearching && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                No coach in this franchise matches “{coachResults.search}”.
              </Typography>
            )}

            {coachSearchItems.length > 0 && (
              <ul style={{ paddingLeft: 0, marginTop: 8, marginBottom: 0 }}>{coachSearchItems}</ul>
            )}

            {coachResults && coachResults.truncated && (
              <Typography variant="caption" color="warning.main" component="div">
                More than {coachResults.max_rows} coaches match. Type more of the name.
              </Typography>
            )}
          </Box>

          {/* 3. The warning, stated where the consequence is. Non-blocking by design. */}
          {effectiveOffRoster && (
            <Alert severity="warning" variant="outlined" sx={{ mt: 1.5, maxWidth: 560 }}>
              <strong>{effectiveOffRoster.coach_display_name}</strong> is not configured on this
              class in Dash. This can still be saved; it will be recorded as a coach who was not on
              the class roster.
              <Button size="small" sx={{ ml: 1 }}
                onClick={() => { setOffRoster(null); setSelectedEmployee(''); setCoachMode(COACH_MODE.UNDECIDED); }}>
                Clear
              </Button>
            </Alert>
          )}
        </Box>
      )}

      <TextField fullWidth size="small" label={noteRequired ? 'Note (required)' : 'Note (optional)'} value={note}
        required={noteRequired} inputProps={{ maxLength: 500 }}
        onChange={(e) => setNote(e.target.value)} sx={{ maxWidth: 560, mb: 1.5, mt: 1.5 }} />

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="contained" size="small" disabled={saving || unchanged || (classConfirmed && !selectedCandidate)} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save review for this evaluation'}
        </Button>
        {unchanged && !saving && (
          <Typography variant="caption" color="text.secondary">Saved — no changes since this review was saved.</Typography>
        )}
        {evaluation.is_reconciled && (
          <Button variant="outlined" size="small" color="secondary" disabled={saving} onClick={() => setConfirmRemove(true)}>
            Remove this evaluation’s review…
          </Button>
        )}
      </Box>

      <ScopedRemoveDialog
        open={confirmRemove}
        title="Remove this evaluation’s review?"
        confirmLabel="Remove this evaluation’s review"
        busy={saving}
        onCancel={() => setConfirmRemove(false)}
        onConfirm={handleRemove}
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          This removes the review of the <strong>{fmtDate(evaluation.class_date)}</strong> evaluation only
          (class: {savedClassText}; coach: {savedCoachText}).
        </Typography>
        <Typography variant="body2">
          {identityRuleActive
            ? <>The identity-wide rule for “{typedName}” <strong>stays in place</strong>.</>
            : <>No identity-wide rule exists for “{typedName}”, and none is changed.</>}
        </Typography>
      </ScopedRemoveDialog>

      {/* Item 11: the scope of THIS save, stated beside it, in three separate claims. */}
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, maxWidth: 620 }}>
        Applies to this evaluation only. It does not change the identity-wide decision above, and it
        {integrationPending
          ? <> is <strong>recorded but does not yet affect CP-06 or CP-08</strong> &mdash; reporting
              reads this in a later release.</>
          : <> becomes <strong>available to reporting after the next successful check-in warehouse
              build</strong>.</>}
      </Typography>
    </Box>
  );
}
